import { ConfirmationResult, RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import { auth } from "../config/firebase.config";

type PhoneAuthWindow = Window &
  typeof globalThis & {
    recaptchaVerifier?: RecaptchaVerifier | null;
    recaptchaWidgetId?: number | null;
    recaptchaContainerId?: string | null;
    confirmationResult?: ConfirmationResult;
    grecaptcha?: {
      reset: (widgetId?: number) => void;
    };
  };

const phoneAuthWindow = window as PhoneAuthWindow;

interface InitRecaptchaOptions {
  containerId: string;
  onVerified?: () => void;
  onExpired?: () => void;
  size?: "invisible" | "normal" | "compact";
}

const clearRecaptchaContainer = (containerId?: string | null) => {
  if (!containerId) {
    return;
  }

  const container = document.getElementById(containerId);
  if (container) {
    container.innerHTML = "";
  }
};

export const clearPhoneRecaptcha = () => {
  if (phoneAuthWindow.recaptchaVerifier) {
    phoneAuthWindow.recaptchaVerifier.clear();
  }

  clearRecaptchaContainer(phoneAuthWindow.recaptchaContainerId);

  phoneAuthWindow.recaptchaVerifier = null;
  phoneAuthWindow.recaptchaWidgetId = null;
  phoneAuthWindow.recaptchaContainerId = null;
};

export const resetPhoneRecaptcha = () => {
  if (
    typeof phoneAuthWindow.recaptchaWidgetId === "number" &&
    phoneAuthWindow.grecaptcha
  ) {
    phoneAuthWindow.grecaptcha.reset(phoneAuthWindow.recaptchaWidgetId);
  }
};

export const initPhoneRecaptcha = async ({
  containerId,
  onVerified,
  onExpired,
  size = "normal",
}: InitRecaptchaOptions): Promise<RecaptchaVerifier> => {
  const container = document.getElementById(containerId);

  if (!container) {
    throw new Error("reCAPTCHA container not found");
  }

  if (
    phoneAuthWindow.recaptchaVerifier &&
    phoneAuthWindow.recaptchaContainerId === containerId
  ) {
    return phoneAuthWindow.recaptchaVerifier;
  }

  clearPhoneRecaptcha();

  const verifier = new RecaptchaVerifier(auth, containerId, {
    size,
    callback: () => {
      onVerified?.();
    },
    "expired-callback": () => {
      onExpired?.();
      resetPhoneRecaptcha();
    },
  });

  const widgetId = await verifier.render();

  phoneAuthWindow.recaptchaVerifier = verifier;
  phoneAuthWindow.recaptchaWidgetId = widgetId;
  phoneAuthWindow.recaptchaContainerId = containerId;

  return verifier;
};

export const sendPhoneOtp = async (
  phoneNumber: string,
  containerId: string
): Promise<ConfirmationResult> => {
  const verifier = await initPhoneRecaptcha({ containerId });

  try {
    const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, verifier);
    phoneAuthWindow.confirmationResult = confirmationResult;
    return confirmationResult;
  } catch (error) {
    resetPhoneRecaptcha();
    throw error;
  }
};
